import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

// Création du contexte
const LanguageContext = createContext();

// Hook personnalisé pour utiliser la langue
export const useLanguage = () => useContext(LanguageContext);

const ALL_LANGUAGES = [
  { code: 'ar', name: 'العربية' },
  { code: 'fr', name: 'Français' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'pt', name: 'Português' },
  { code: 'de', name: 'Deutsch' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'it', name: 'Italiano' },
  { code: 'zh-CN', name: '中文' },
  { code: 'ru', name: 'Русский' },
];

const supportedLngs = i18n.options?.supportedLngs || ['ar'];
const defaultLng = supportedLngs.includes('ar') ? 'ar' : supportedLngs[0];
const availableLanguages =
  supportedLngs.length === 1 && supportedLngs[0] === 'ar'
    ? [{ code: 'ar', name: 'العربية' }]
    : ALL_LANGUAGES.filter(({ code }) => supportedLngs.includes(code));

// Provider de la langue
export const LanguageProvider = ({ children }) => {
  const { i18n: i18nInstance, t } = useTranslation();

  const [language, setLanguage] = useState(defaultLng);

  const isRTL = language === 'ar';

  const changeLanguage = (lang) => {
    if (!supportedLngs.includes(lang)) return;

    i18nInstance.changeLanguage(lang);
    setLanguage(lang);

    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;

    const settings = JSON.parse(localStorage.getItem('qawaem-settings') || '{}');
    settings.invoicing = settings.invoicing || {};
    settings.invoicing.language = lang;
    localStorage.setItem('qawaem-settings', JSON.stringify(settings));
  };

  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem('language');
      const settings = JSON.parse(localStorage.getItem('qawaem-settings') || '{}');
      const settingsLanguage = settings.invoicing?.language;
      const preferredLanguage = savedLanguage || settingsLanguage || i18nInstance.language || defaultLng;
      const resolvedLanguage = supportedLngs.includes(preferredLanguage) ? preferredLanguage : defaultLng;
      if (resolvedLanguage !== language) {
        changeLanguage(resolvedLanguage);
      }
    } catch (error) {
      console.error('Error loading language preference:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (i18nInstance.language !== language) {
      setLanguage(i18nInstance.language);
    }
  }, [i18nInstance.language]);

  const value = {
    language,
    isRTL,
    changeLanguage,
    availableLanguages,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
